import React from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { ContinueModalProps } from "./types";
import { formatUrlWithProtocol, generateCloudinaryImageUrl, getLocalStorage } from "../../utils/common-function";
import mixpanel from "../../services/mixpanelService";
import useColinkExist from "../../services/hooks/useColinkExist";
import { SampleEligibleBrandsData, Stashlink } from "../../types";
import FollowingCheck from "../../assets/images/following-check.svg";
import ExternalLinkIcon from "./../../assets/images/take-action-external-link.png";
import { routes } from "../../routes/routes.constant";
import { useHistory } from "react-router";
import { isAmazonCampaign, isNativeWebview, MAX_GAME_PLAY_COUNT } from "../../common/constants";
import { RetryIcon } from "../../modules/campaign/retryIcon";
import { ShareIcon } from "../../modules/campaign/shareIcon";

const ContinueModal: React.FC<ContinueModalProps> = (props: ContinueModalProps) => {
  const { isOpen, onClose, campaign, openShareModal, navigateToStashAgain, navigateToColink, openWaitlistModal } = props;
  const gamePlayCount = getLocalStorage("gamePlayCount") as number;
  const stashlink = getLocalStorage("stashlink") as Stashlink;
  const userId = getLocalStorage("userId") as string;
  const brandsData = getLocalStorage("brandsData") as SampleEligibleBrandsData;
  const onboardingFeatureFlag = useFeatureIsOn("onboarding-flow");
  const { colinkExistData } = useColinkExist(stashlink?.id || "");
  const navigation = useHistory();
  const isAmazon = isAmazonCampaign(stashlink.id);
  if (!isOpen) return null;

  const playAgainButton = (
    <button
      onClick={() => {
        const hasOnlyOneBrand = brandsData?.sampleEligibleBrands?.topEligibleBrands?.length === 1;
        mixpanel.track("continue-modal-play-again-clicked", {
          redirectTo: hasOnlyOneBrand ? "brand" : "stashlink"
        });
        if (isAmazon) {
          navigation.replace({ ...location, state: {} });
          navigation.replace(routes.bubblePop, { fromValidFlow: true });
          return;
        }

        if (hasOnlyOneBrand) {
          navigation.push({
            pathname: routes.sponsorDetails,
            state: { from: location.pathname, fromValidFlow: true, showMatchModal: false }
          });
        } else {
          navigateToStashAgain();
        }
      }}
      className="w-full gap-x-4 px-3 py-2 items-center justify-start flex flex-row bg-grey/20 rounded-xl border-t-2 border-b-4 border-x-2 border-[#90a8c1]/70 tracking-0.56 text-base-white text-sm leading-tight-16.8">
      <RetryIcon />
      <div className="flex flex-col">
        <div className="text-left font-black font-nunito text-base mb-0.5 select-none">
          {isAmazon ? "Unlock more trees 🌳" : "Play again"}
        </div>
        <div className="text-left text-xs select-none font-semibold font-nunito text-light-grey">
          {isAmazon ? "Play again to increase your impact" : "Raise more money when you play a second time."}
        </div>
      </div>
    </button>
  );

  const plantAnotherTreeCampaignButton = (
    <button
      className="w-full gap-x-4 bg-soft-black px-3 py-2 text-sm rounded-xl text-primary-l1 border-primary-l1 border-t-2 border-b-4 border-x-2 border-grey/70 flex align-center justify-start tracking-wide leading-tight-16.8 items-center"
      onClick={() => {
        const hasOnlyOneBrand = brandsData?.sampleEligibleBrands?.topEligibleBrands?.length === 1;
        mixpanel.track("continue-modal-play-again-clicked", {
          redirectTo: hasOnlyOneBrand ? "brand" : "stashlink"
        });
        navigation.replace({ ...location, state: {} });
        navigation.replace(routes.bubblePop, { fromValidFlow: true });
        return;
      }}>
      <RetryIcon className="text-[#D7FF3E]" />
      <div className="flex flex-col">
        <div className="text-left font-black mb-0.5 font-nunito text-base select-none">Unlock more trees 🌳</div>
        <div className="text-left text-xs select-none font-semibold font-nunito"> Play again to increase your impact</div>
      </div>
    </button>
  );

  const returnToCampaignButton = (
    <div
      onClick={() => {
        mixpanel.track("continue-modal-return-clicked");
        navigateToStashAgain();
      }}
      className="w-full gap-x-2 py-4 px-4 items-center justify-center flex flex-row rounded-xl tracking-0.56 text-base-white text-sm leading-tight-16.8">
      <div className="flex flex-col">
        <div className="text-center font-medium font-primary text-base select-none">Back to campaign</div>
      </div>
    </div>
  );

  return (
    <div
      className={`fixed inset-0 bg-grey-op40/70 flex items-start justify-center z-20 backdrop-blur-10.5 ${isNativeWebview() ? "pt-[160px]" : "items-center"}`}
      onClick={onClose}>
      <div className={`text-base-white rounded-3xl w-[90%] relative px-7 py-7 bg-black ${isNativeWebview() ? '': 'mt-12'}`}>
        <div className="space-y-5">
          <div className="font-primary font-bold text-lg w-full text-center leading-snug text-neutral-50 select-none">
            More ways to help
          </div>
          <div className="space-y-3">
            {isAmazon && gamePlayCount <= 1 && plantAnotherTreeCampaignButton}
            <button
              className={`w-full gap-x-4 bg-soft-black px-3 py-2 text-sm rounded-xl ${isAmazon && gamePlayCount > 1 ? "border-primary-l1 text-primary-l1" : " border-[#90a8c1]/70 bg-grey/20"}  border-t-2 border-b-4 border-x-2 border-grey/70 flex align-center justify-start tracking-wide leading-tight-16.8 items-center`}
              onClick={() => {
                onClose();
                openShareModal();
                mixpanel.track("continue-modal-share-clicked");
              }}>
              <div className="block">
                <ShareIcon />
              </div>
              <div className="flex flex-col">
                <div className="text-left w-full font-black font-nunito text-base flex justify-between mb-0.5 select-none">Share</div>
                {isAmazon ? (
                  <div className="text-left text-xs select-none font-nunito font-semibold">
                    Invite friends to stash with you and play to plant even more trees!
                  </div>
                ) : (
                  <div className="text-left text-xs select-none font-nunito font-semibold">
                    Sharing to your social networks raises up to <b>5x more</b>
                  </div>
                )}
              </div>
            </button>
            {!isAmazon && (
              <>{!onboardingFeatureFlag ? <>{gamePlayCount < MAX_GAME_PLAY_COUNT ? playAgainButton : null}</> : playAgainButton}</>
            )}
            {isAmazon && gamePlayCount > 1 && playAgainButton}
            {onboardingFeatureFlag && (
              <div>
                {colinkExistData || stashlink.user.id === userId ? (
                  <div>
                    <button
                      onClick={() => mixpanel.track("team-up-button-clicked")}
                      className="w-full gap-x-4 px-3 py-2 items-center justify-start flex flex-row bg-grey/20 rounded-xl border-t-2 border-b-4 border-x-2 border-[#90a8c1]/70 tracking-0.56 text-base-white text-sm leading-tight-16.8 select-none">
                      <div className="text-center font-bold font-primary text-base">Teamed up</div>
                      <span>
                        <img src={FollowingCheck} alt="Following Check"></img>
                      </span>
                    </button>
                  </div>
                ) : (
                  <button
                    className="w-full gap-x-4 px-3 py-2 items-center justify-start flex flex-row bg-grey/20 rounded-xl border-t-2 border-b-4 border-x-2 border-[#90a8c1]/70 tracking-0.56 text-base-white text-sm leading-tight-16.8"
                    onClick={navigateToColink}>
                    <img
                      src={generateCloudinaryImageUrl(
                        stashlink?.parentStashlink
                          ? (stashlink.parentStashlink.user?.profilePhoto ?? "")
                          : (stashlink.user?.profilePhoto ?? ""),
                        "68",
                        "68"
                      )}
                      width="48px"
                      height="48px"
                      alt="Logo"
                      className="rounded-full pure-black border-[1px] border-solid border-[#90A8C1]/15"
                    />
                    <div className="flex flex-col">
                      <div className="text-left font-bold font-primary text-sm mb-0.5 text-neutral-50 select-none">Team up</div>
                      <div className="text-left text-xs text-light-grey select-none">
                        Create a stashlink together with{" "}
                        {stashlink?.parentStashlink ? stashlink.parentStashlink.user?.fullName : stashlink.user?.fullName} to raise more.
                      </div>
                    </div>
                  </button>
                )}
              </div>
            )}
            {isAmazon && (
              <button
                onClick={() => {
                  mixpanel.track("waitlist-action-clicked", {
                    location: "thank-you-page",
                    campaignName: campaign?.title,
                    charityName: campaign?.charity?.name
                  });
                  openWaitlistModal();
                }}
                className="w-full gap-x-4 px-3 py-2 items-center justify-start flex flex-row bg-grey/20 rounded-xl border-t-2 border-b-4 border-x-2 border-[#90a8c1]/70 tracking-0.56 text-base-white text-sm leading-tight-16.8">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="min-w-[48px]">
                  <g clipPath="url(#clip0_2678_111709)">
                    <rect x="6" y="6" width="42" height="42" fill="#F8F8F7" />
                    <path
                      d="M34.4917 1.53701C35.3682 0.660558 36.8034 0.659954 37.6799 1.53632C38.5556 2.41193 38.5634 3.8417 37.6799 4.7252L18.6633 23.7418C18.3089 24.0963 18.3083 24.682 18.6626 25.0366C19.0184 25.3923 19.5986 25.396 19.9581 25.0366L24.7103 20.2843C26.83 18.1646 30.2865 18.1641 32.4063 20.2836C34.5261 22.4034 34.5261 25.8605 32.4063 27.9803L25.3753 35.0113L14.1686 46.2056C13.2861 47.0881 11.8513 47.0879 10.9687 46.2056C10.0861 45.323 10.0854 43.8876 10.968 43.005L28.973 25C29.3274 24.6455 29.3279 24.0598 28.9737 23.7052C28.6179 23.3494 28.0377 23.3458 27.6782 23.7052L22.9073 28.4761C20.7876 30.5959 17.3429 30.5959 15.2231 28.4761C13.104 26.3567 13.1043 22.9135 15.2307 20.7995L15.23 20.7988L34.4917 1.53701Z"
                      fill="#1F1F23"
                      stroke="#1F1F23"
                      strokeWidth="0.819"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_2678_111709">
                      <rect x="6" y="6" width="36" height="36" rx="18" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <div className="flex flex-col">
                  <div className="text-left w-full font-black font-nunito text-base flex justify-between mb-0.5 select-none">
                    Get early access
                  </div>
                  <div className="text-left font-semibold text-xs select-none font-nunito text-light-grey">
                    Join the waitlist for the next play to fund campaign.
                  </div>
                </div>
              </button>
            )}
            {campaign?.callToActionUrl?.length && !isAmazon ? (
              <button
                onClick={() => {
                  mixpanel.track("take-action-clicked", {
                    location: "thank-you-page",
                    campaignName: campaign?.title,
                    charityName: campaign?.charity?.name
                  });
                  window.open(formatUrlWithProtocol(campaign?.callToActionUrl), "_blank");
                }}
                className="w-full gap-x-4 px-3 py-2 items-center justify-start flex flex-row bg-grey/20 rounded-xl border-t-2 border-b-4 border-x-2 border-[#90a8c1]/70 tracking-0.56 text-base-white text-sm leading-tight-16.8">
                <img
                  src={generateCloudinaryImageUrl(campaign?.charity?.imageUrl || "", "68", "68")}
                  alt="Logo"
                  width="48px"
                  height="48px"
                  className="rounded-full pure-black border-2 border-solid border-base-white"
                />
                <div className="flex flex-col">
                  <div className="text-left w-full font-black font-nunito text-base flex justify-between mb-0.5 select-none">
                    Take action
                    <div>
                      <img src={ExternalLinkIcon} alt={ExternalLinkIcon} className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="text-left font-semibold text-xs select-none font-nunito text-light-grey">
                    Discover other ways to support this cause.
                  </div>
                </div>
              </button>
            ) : null}
            {gamePlayCount >= MAX_GAME_PLAY_COUNT ? returnToCampaignButton : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContinueModal;
